/**
 * TenantPendingAssignment — shown to a freshly-created tenant (typically a
 * brand-new Google sign-in) whose account has no property_id yet.
 *
 * The user can't use the app meaningfully until a Property Manager or admin
 * assigns them to a property. This screen welcomes them, explains the
 * situation, and gives them clear next-step contacts.
 */
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Mail, Phone, LogOut, Building, ShieldCheck, Clock } from 'lucide-react';
import NECSLogo from './NECSLogo';

export default function TenantPendingAssignment({ user, onLogout }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-6" data-testid="tenant-pending-assignment">
      {/* Compact top bar */}
      <div className="w-full max-w-2xl flex justify-between items-center mb-6">
        <NECSLogo size="sm" align="left" dataTestId="pending-logo" />
        <Button
          variant="outline"
          size="sm"
          onClick={onLogout}
          className="border-zinc-300"
          data-testid="pending-logout-btn"
        >
          <LogOut className="h-4 w-4 mr-1" /> Sign out
        </Button>
      </div>

      <Card className="w-full max-w-2xl p-8 sm:p-10 bg-white border-2 border-[#FFD60A] shadow-xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-12 w-12 rounded-full bg-[#FFD60A] flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-zinc-900" />
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A962] font-bold">
            Welcome to Noble Empire
          </p>
        </div>

        <h1
          className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-3"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          data-testid="pending-headline"
        >
          Hi {user?.name || 'there'} — your account is almost ready
        </h1>

        <p className="text-zinc-600 leading-relaxed mb-6">
          We've created your resident account, but it isn't connected to a property yet. Your
          Property Manager or our Noble Empire team needs to assign your unit before you can
          start checking trash placements, requesting bulk pickup, and tracking nightly service.
        </p>

        <div className="rounded-xl p-4 mb-6 bg-zinc-50 border border-zinc-200">
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-3">
            What you can do right now
          </p>
          <ol className="space-y-3 text-sm text-zinc-700">
            <li className="flex items-start gap-3">
              <Building className="h-5 w-5 text-[#C9A962] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-zinc-900">Tell your Property Manager</p>
                <p className="text-zinc-600 text-xs">
                  Share the email you signed up with so they can match you to your unit:
                  <strong className="ml-1 text-zinc-900" data-testid="pending-user-email">{user?.email}</strong>
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-[#C9A962] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-zinc-900">Or call Noble Empire support</p>
                <p className="text-zinc-600 text-xs">We can connect you to your property in a few minutes.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-[#C9A962] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-zinc-900">Sign back in once you're assigned</p>
                <p className="text-zinc-600 text-xs">
                  As soon as your unit is linked, log back in and the resident dashboard will unlock automatically.
                </p>
              </div>
            </li>
          </ol>
        </div>

        <div className="flex flex-wrap gap-3">
          <a
            href="tel:+18882413077"
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm text-zinc-900"
            style={{ backgroundColor: '#FFD60A' }}
            data-testid="pending-call-btn"
          >
            <Phone className="h-4 w-4" /> Call +1 (888) 241-3077
          </a>
          <a
            href={`mailto:support@nobleempirecs.com?subject=New resident account — please assign property&body=Hi Noble Empire team,%0D%0A%0D%0AI just signed up using ${encodeURIComponent(user?.email || '')}. Please connect my account to my property.%0D%0A%0D%0AProperty/community name: %0D%0AUnit number: %0D%0A%0D%0AThanks!`}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm border-2"
            style={{ borderColor: '#C9A962', color: '#C9A962' }}
            data-testid="pending-email-btn"
          >
            <Mail className="h-4 w-4" /> Email support
          </a>
        </div>

        <p className="text-xs text-zinc-500 mt-6">
          2450 Lakeside Pkwy, Ste 150, Flower Mound, TX 75022 · Mon–Fri 8:00 AM – 6:00 PM CT
        </p>
      </Card>
    </div>
  );
}
